// File: frontend/src/pages/Unsubscribe/Unsubscribe.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNewsletter } from '../../hooks/useNewsletter';
import Button from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import { FiAlertTriangle, FiMail, FiCheck } from 'react-icons/fi';
import styles from './Unsubscribe.module.css';

const Unsubscribe = () => {
  const { t } = useLanguage();
  const { unsubscribe, isLoading, error, success, clearError } = useNewsletter();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [emailAddress, setEmailAddress] = useState('');
  const [reason, setReason] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccessFromUrl, setIsSuccessFromUrl] = useState(false);
  
  // Parse email from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get('email');
    const successParam = params.get('success');
    
    if (email) {
      const decodedEmail = decodeURIComponent(email);
      setEmailAddress(decodedEmail);
      setIsConfirming(true);
      
      if (successParam === 'true') {
        setIsSuccessFromUrl(true);
      }
    } else {
      // If no email in URL, redirect to home page
      navigate('/');
    }
  }, [location, navigate]);
  
  const handleReasonChange = (e) => {
    setReason(e.target.value);
  };
  
  const handleUnsubscribe = async () => {
    setIsProcessing(true);
    
    try {
      await unsubscribe({
        emailAddress,
        reason
      });
    } catch (err) {
      // Error handling is done in the hook
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/');
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.unsubscribeContainer}>
        <div className={styles.heroSection}>
          <h1 className={styles.title}>
            {success || isSuccessFromUrl 
              ? t('newsletter.unsubscribe.success_title', 'Unsubscribed Successfully') 
              : t('newsletter.unsubscribe.title', 'Unsubscribe from Newsletter')}
          </h1>
          <div className={styles.titleUnderline}></div>
        </div>

        <div className={styles.contentSection}>
          {error && (
            <Alert 
              type="error" 
              titleKey="newsletter.error.title"
              messageKey={`newsletter.errors.${error}`}
              onClose={clearError}
            />
          )}
          
          {success || isSuccessFromUrl ? (
            <div className={styles.successContainer}>
              <div className={styles.successIcon}>
                <FiCheck size={40} />
              </div>
              <h2 className={styles.successTitle}>
                {t('newsletter.unsubscribe.success_heading', 'You have been unsubscribed')}
              </h2>
              <p className={styles.successMessage}>
                {t('newsletter.unsubscribe.success_message', 'You will no longer receive our newsletter emails. We\'re sorry to see you go!')}
              </p>
              
              <div className={styles.actionButtons}>
                <Button 
                  variant="primary" 
                  size="medium" 
                  onClick={() => navigate('/subscribe')}
                >
                  {t('newsletter.unsubscribe.resubscribe', 'Resubscribe')}
                </Button>
                <Button 
                  variant="secondary" 
                  size="medium" 
                  onClick={() => navigate('/')}
                >
                  {t('newsletter.unsubscribe.go_home', 'Go to Homepage')}
                </Button>
              </div>
              
              <div className={styles.followSection}>
                <p className={styles.followText}>
                  {t('newsletter.unsubscribe.follow_us', 'You can still follow us on social media:')}
                </p>
                <div className={styles.socialLinks}>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>Facebook</a>
                  <a href="https://x.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>X (Twitter)</a>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>LinkedIn</a>
                </div>
              </div>
            </div>
          ) : isConfirming ? (
            <div className={styles.confirmContainer}>
              <div className={styles.confirmIcon}>
                <FiAlertTriangle size={40} />
              </div>
              <h2 className={styles.confirmTitle}>
                {t('newsletter.unsubscribe.confirm_title', 'Confirm Unsubscription')}
              </h2>
              <p className={styles.confirmMessage}>
                {t('newsletter.unsubscribe.confirm_message', 'Are you sure you want to unsubscribe from our newsletter? You will no longer receive updates from us.')}
              </p>
              <div className={styles.emailDisplay}>
                <FiMail className={styles.emailIcon} />
                <span className={styles.emailText}>{emailAddress}</span>
              </div>
              
              <div className={styles.reasonContainer}>
                <label htmlFor="unsubscribe-reason" className={styles.reasonLabel}>
                  {t('newsletter.unsubscribe.reason_label', 'Would you mind telling us why you\'re leaving? (Optional)')}
                </label>
                <select 
                  id="unsubscribe-reason" 
                  className={styles.reasonSelect}
                  value={reason}
                  onChange={handleReasonChange}
                >
                  <option value="">
                    {t('newsletter.unsubscribe.reason_select', '-- Select a reason --')}
                  </option>
                  <option value="too_many_emails">
                    {t('newsletter.unsubscribe.reason_too_many', 'Too many emails')}
                  </option>
                  <option value="not_relevant">
                    {t('newsletter.unsubscribe.reason_not_relevant', 'Content not relevant')}
                  </option>
                  <option value="never_signed_up">
                    {t('newsletter.unsubscribe.reason_never_signed', 'I never signed up')}
                  </option>
                  <option value="other">
                    {t('newsletter.unsubscribe.reason_other', 'Other reason')}
                  </option>
                </select>
              </div>
              
              <div className={styles.actionButtons}>
                <Button 
                  variant="primary" 
                  size="medium" 
                  onClick={handleUnsubscribe}
                  isLoading={isLoading || isProcessing}
                >
                  {t('newsletter.unsubscribe.confirm_button', 'Confirm Unsubscribe')}
                </Button>
                <Button 
                  variant="secondary" 
                  size="medium" 
                  onClick={handleCancel}
                >
                  {t('newsletter.unsubscribe.cancel_button', 'Cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.redirectContainer}>
              <p>{t('newsletter.unsubscribe.redirecting', 'Redirecting to homepage...')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;